import * as monaco from 'monaco-editor';
import { MutableRefObject } from 'react';
import { buildExplicitImportEdits } from '../../Editor/Monaco/pythonImports/service';
import { getLineStarts } from '../../Editor/Monaco/pythonImports/sourceAnalysis';
import type { OffsetTextEdit } from '../../Editor/Monaco/pythonImports/types';
import { findRobotStartLine } from '../../../utils/monacoRegionUtils';
import { toPythonSnakeCase } from '../../../utils/pythonIdentifierUtils';
import { VAR_ASSIGN_RE } from '../../../utils/pythonSourceScanUtils';
import type { CodeSnippetCodeByDropZone, CodeSnippetDropZone, CodeSnippetDropZoneMode, CodeSnippetImport } from '../types';
import { hasDriveBaseDefinition, isDriveBaseConstructorSnippet } from './driveBaseSingleton';

/**
 * Ensures the variable name in a snippet like "intake = Intake()" is unique
 * within the given model. If "intake" exists, returns "intake2 = Intake()", etc.
 */
export function uniquifyVariableName(code: string, model: monaco.editor.ITextModel | null): string {
  if (!model) return code;

  const lines = code.split('\n');
  const firstLine = lines[0];
  if (!firstLine) return code;

  const match = firstLine.match(/^(\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*=/);
  if (!match) return code;

  const baseName = match[2];
  const escapedBase = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const codeContent = model.getValue();
  const assignRegex = new RegExp(`^\\s*${escapedBase}(\\d*)\\s*=`, 'gm');

  const existing = new Set<string>();
  let m;
  while ((m = assignRegex.exec(codeContent)) !== null) {
    const suffix = m[1] || '';
    existing.add(baseName + suffix);
  }

  let candidate = baseName;
  let n = 2;
  while (existing.has(candidate)) {
    candidate = `${baseName}${n}`;
    n++;
  }

  if (candidate === baseName) return code;

  const newFirstLine = firstLine.replace(new RegExp(`^(\\s*)${escapedBase}(\\s*=)`), `$1${candidate}$2`);
  lines[0] = newFirstLine;
  return lines.join('\n');
}

function findClosestReceiverVariableAboveTarget(code: string, targetLine: number, receiverClassName: string): string | null {
  const lines = code.split(/\r?\n/);
  const maxDeclarationLineIndex = Math.min(targetLine - 2, lines.length - 1);
  if (maxDeclarationLineIndex < 0) {
    return null;
  }

  for (let lineIndex = maxDeclarationLineIndex; lineIndex >= 0; lineIndex--) {
    const line = lines[lineIndex] ?? '';
    if (/^\s/.test(line)) {
      continue;
    }

    const match = line.match(VAR_ASSIGN_RE);
    if (match?.[1] && match[2] === receiverClassName) {
      return match[1];
    }
  }

  return null;
}

/**
 * Rewrites receiver-based snippets, e.g. `drive_base.drive_for_time_action(...)`,
 * to use the closest compatible declaration above the target insertion line.
 */
export function resolveSnippetReceiverName(
  code: string,
  model: monaco.editor.ITextModel | null | undefined,
  targetLine: number,
  receiverClassName?: string
): string {
  if (!receiverClassName || !model || !Number.isFinite(targetLine)) {
    return code;
  }

  const receiverVariable = findClosestReceiverVariableAboveTarget(model.getValue(), targetLine, receiverClassName);
  if (!receiverVariable) {
    return code;
  }

  const defaultReceiver = toPythonSnakeCase(receiverClassName);
  return code.replace(new RegExp(`^(\\s*)${defaultReceiver}(\\s*\\.)`), `$1${receiverVariable}$2`);
}

export function resolveSnippetReceiverNamesByDropZone(
  codeByDropZone: CodeSnippetCodeByDropZone | undefined,
  model: monaco.editor.ITextModel | null | undefined,
  targetLine: number,
  receiverClassName?: string
): CodeSnippetCodeByDropZone | undefined {
  if (!codeByDropZone || !receiverClassName) {
    return codeByDropZone;
  }

  return Object.fromEntries(
    Object.entries(codeByDropZone).map(([dropZone, code]) => [dropZone, resolveSnippetReceiverName(code, model, targetLine, receiverClassName)])
  );
}

function uniquifyTopLevelSetupCodeByDropZone(
  codeByDropZone: CodeSnippetCodeByDropZone | undefined,
  model: monaco.editor.ITextModel | null | undefined
): CodeSnippetCodeByDropZone | undefined {
  const setupCode = codeByDropZone?.['top-level-setup'];
  if (!codeByDropZone || !setupCode || !model) {
    return codeByDropZone;
  }

  return {
    ...codeByDropZone,
    'top-level-setup': uniquifyVariableName(setupCode, model),
  };
}

/**
 * Adds one blank line above and below the inserted block on drop, only when needed (avoids double blanks).
 * Does not add blank above when the previous line is a comment or already blank.
 * Call this right after committing a valid drop.
 */
export function ensureSpacingAroundDrop(editor: monaco.editor.IStandaloneCodeEditor, startLine: number, numLines: number): void {
  const model = editor.getModel();
  if (!model) return;

  const prevLineContent = startLine <= 1 ? '' : model.getLineContent(startLine - 1);
  const prevLineIsBlankOrComment = startLine <= 1 || prevLineContent.trim().length === 0 || /^\s*#/.test(prevLineContent);
  const lineAfterBlock = startLine + numLines;
  const lineAfterIsBlank = lineAfterBlock > model.getLineCount() || model.getLineContent(lineAfterBlock).trim().length === 0;
  const needBlankAbove = !prevLineIsBlankOrComment;
  const needBlankBelow = !lineAfterIsBlank;

  if (!needBlankAbove && !needBlankBelow) return;

  // Apply "below" edit first so line numbers stay correct (inserting above would shift them)
  const edits: monaco.editor.IIdentifiedSingleEditOperation[] = [];
  if (needBlankBelow) {
    edits.push({ range: new monaco.Range(lineAfterBlock, 1, lineAfterBlock, 1), text: '\n', forceMoveMarkers: true });
  }
  if (needBlankAbove) {
    edits.push({ range: new monaco.Range(startLine, 1, startLine, 1), text: '\n', forceMoveMarkers: true });
  }
  editor.pushUndoStop();
  editor.executeEdits('drop.spacing', edits);
  editor.pushUndoStop();
}

export function waitUntil(condition: () => boolean, timeout = 1000, interval = 10): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (condition()) {
        resolve();
      } else if (Date.now() - start >= timeout) {
        reject(new Error('Timeout waiting for condition'));
      } else {
        setTimeout(check, interval);
      }
    };
    check();
  });
}

/**
 * Returns the 1-based line number of the last import (or shebang/comment/blank in import block).
 * Scans from the top; the last line that is an import statement.
 */
function findLastImportLine(model: monaco.editor.ITextModel): number {
  const lines = model.getLineCount();
  let lastImportLine = 0;
  for (let i = 1; i <= lines; i++) {
    const content = model.getLineContent(i);
    const isBlank = content.trim().length === 0;
    const isComment = /^\s*#/.test(content);
    const isImport = /^\s*(import\s+\S+|from\s+\S+\s+import\s+.+)/.test(content);
    const isShebang = i === 1 && content.startsWith('#!');
    if (isShebang || isComment || isBlank || isImport) {
      if (isImport) lastImportLine = i;
      continue;
    }
    break;
  }
  return lastImportLine;
}

/**
 * Valid drop zone for mechanism/electronic: below imports, above RobotBase().start, not inside () of constructor/method.
 */
function isValidMechanismElectronicDropZone(model: monaco.editor.ITextModel, targetLine: number): boolean {
  const lastImport = findLastImportLine(model);
  if (targetLine <= lastImport) return false;

  const robotStartLine = findRobotStartLine(model);
  if (robotStartLine !== null && targetLine >= robotStartLine) return false;

  if (isInsideAnyGroup(model, targetLine)) return false;

  return true;
}

export function isValidSnippetDropZone(
  model: monaco.editor.ITextModel,
  targetLine: number,
  snippetType?: string,
  dropZone?: CodeSnippetDropZoneMode,
  snippetCode?: string,
  frameworkClassName?: string
): boolean {
  if (isDriveBaseConstructorSnippet(snippetCode, frameworkClassName) && hasDriveBaseDefinition(model.getValue())) {
    return false;
  }

  if (dropZone === 'contextual') {
    return isInsideFrameworkActionGroup(model, targetLine) || isValidMechanismElectronicDropZone(model, targetLine);
  }

  const isMechanismOrElectronic = snippetType === 'mechanism' || snippetType === 'electronic';
  const usesTopLevelSetupDropZone = dropZone === 'top-level-setup' || (!dropZone && isMechanismOrElectronic);
  return usesTopLevelSetupDropZone ? isValidMechanismElectronicDropZone(model, targetLine) : isInsideAnyGroup(model, targetLine);
}

export function resolveSnippetDropZoneForTarget(
  model: monaco.editor.ITextModel,
  targetLine: number,
  snippetType?: string,
  dropZone?: CodeSnippetDropZoneMode,
  snippetCode?: string,
  frameworkClassName?: string
): CodeSnippetDropZone | null {
  if (isDriveBaseConstructorSnippet(snippetCode, frameworkClassName) && hasDriveBaseDefinition(model.getValue())) {
    return null;
  }

  if (dropZone === 'contextual') {
    if (isInsideFrameworkActionGroup(model, targetLine)) return 'action-group';
    if (isValidMechanismElectronicDropZone(model, targetLine)) return 'top-level-setup';
    return null;
  }

  if (!isValidSnippetDropZone(model, targetLine, snippetType, dropZone, snippetCode, frameworkClassName)) {
    return null;
  }

  const isMechanismOrElectronic = snippetType === 'mechanism' || snippetType === 'electronic';
  return dropZone === 'top-level-setup' || (!dropZone && isMechanismOrElectronic) ? 'top-level-setup' : 'action-group';
}

export interface ResolveSnippetCodeForTargetParams {
  code: string | undefined;
  codeByDropZone?: CodeSnippetCodeByDropZone;
  model: monaco.editor.ITextModel | null | undefined;
  targetLine: number;
  snippetType?: string;
  dropZone?: CodeSnippetDropZoneMode;
  receiverClassName?: string;
  frameworkClassName?: string;
  resolvedDropZone?: CodeSnippetDropZone | null;
  uniquifyTopLevelSetup?: boolean;
}

export interface ResolvedSnippetCodeForTarget {
  code: string | undefined;
  codeByDropZone: CodeSnippetCodeByDropZone | undefined;
  codeForTarget: string | undefined;
  resolvedDropZone: CodeSnippetDropZone | null;
}

export function resolveSnippetCodeForTarget({
  code,
  codeByDropZone,
  model,
  targetLine,
  snippetType,
  dropZone,
  receiverClassName,
  frameworkClassName,
  resolvedDropZone,
  uniquifyTopLevelSetup = false,
}: ResolveSnippetCodeForTargetParams): ResolvedSnippetCodeForTarget {
  const targetDropZone =
    resolvedDropZone ?? (model ? resolveSnippetDropZoneForTarget(model, targetLine, snippetType, dropZone, code, frameworkClassName) : null);
  const receiverResolvedCode = typeof code === 'string' ? resolveSnippetReceiverName(code, model, targetLine, receiverClassName) : code;
  const receiverResolvedCodeByDropZone = resolveSnippetReceiverNamesByDropZone(codeByDropZone, model, targetLine, receiverClassName);
  const preparedCode =
    typeof receiverResolvedCode === 'string' && uniquifyTopLevelSetup
      ? uniquifyVariableName(receiverResolvedCode, model ?? null)
      : receiverResolvedCode;
  const preparedCodeByDropZone = uniquifyTopLevelSetup
    ? uniquifyTopLevelSetupCodeByDropZone(receiverResolvedCodeByDropZone, model)
    : receiverResolvedCodeByDropZone;

  return {
    code: preparedCode,
    codeByDropZone: preparedCodeByDropZone,
    codeForTarget: (targetDropZone && preparedCodeByDropZone?.[targetDropZone]) || preparedCode,
    resolvedDropZone: targetDropZone,
  };
}

function isInsideAnyGroup(model: monaco.editor.ITextModel, line: number): boolean {
  const total = model.getLineCount();
  for (let i = 1; i <= total; i++) {
    const startLineContent = model.getLineContent(i);
    if (/\(\s*$/.test(startLineContent)) {
      let depth = 1;
      for (let j = i + 1; j <= total; j++) {
        const ln = model.getLineContent(j);
        let openCount = (ln.match(/\(/g) || []).length;
        let closeCount = (ln.match(/\)/g) || []).length;
        if (/^\s*\)\s*,?\s*$/.test(ln)) {
          openCount = 0;
          closeCount = Math.max(closeCount, 1);
        }
        depth += openCount - closeCount;
        if (depth <= 0) {
          // Inside if target is after the '(' line and before or on the ')' line
          if (line > i && line <= j) return true;
          i = j; // skip ahead
          break;
        }
      }
    }
  }
  return false;
}

const FRAMEWORK_ACTION_GROUP_NAMES = ['SequentialActionGroup', 'ParallelActionGroup', 'ParallelDeadlineGroup', 'ParallelRaceGroup'] as const;

const FRAMEWORK_ACTION_GROUP_CALL_RE = new RegExp(
  String.raw`^\s*(?:[A-Za-z_]\w*\s*=\s*)?(?:return\s+)?(?:\w+\.)?(?:${FRAMEWORK_ACTION_GROUP_NAMES.join('|')})\s*\(\s*$`
);

function isInsideFrameworkActionGroup(model: monaco.editor.ITextModel, line: number): boolean {
  const total = model.getLineCount();
  for (let i = 1; i <= total; i++) {
    const startLineContent = model.getLineContent(i);
    if (!FRAMEWORK_ACTION_GROUP_CALL_RE.test(startLineContent)) {
      continue;
    }

    let depth = 1;
    for (let j = i + 1; j <= total; j++) {
      const ln = model.getLineContent(j);
      let openCount = (ln.match(/\(/g) || []).length;
      let closeCount = (ln.match(/\)/g) || []).length;
      if (/^\s*\)\s*,?\s*$/.test(ln)) {
        openCount = 0;
        closeCount = Math.max(closeCount, 1);
      }
      depth += openCount - closeCount;
      if (depth <= 0) {
        if (line > i && line <= j) return true;
        i = j;
        break;
      }
    }
  }
  return false;
}

function findPrevNonEmpty(model: monaco.editor.ITextModel, targetLine: number): { num: number; content: string } | null {
  for (let i = targetLine - 1; i >= 1; i--) {
    const c = model.getLineContent(i);
    if (c.trim().length > 0) return { num: i, content: c };
  }
  return null;
}

function findNextNonEmpty(model: monaco.editor.ITextModel, targetLine: number): { num: number; content: string } | null {
  const max = model.getLineCount();
  for (let i = targetLine; i <= max; i++) {
    const c = model.getLineContent(i);
    if (c.trim().length > 0) return { num: i, content: c };
  }
  return null;
}

export interface InsertPreviewSnippetParams {
  editor: monaco.editor.IStandaloneCodeEditor;
  textToInsert: string;
  cursorPos: monaco.Position;
  lastPreviewRef: MutableRefObject<{ line: number; numLines?: number; isValidZone?: boolean; resolvedDropZone?: CodeSnippetDropZone | null } | null>;
  draggingDecorationRef: MutableRefObject<monaco.editor.IEditorDecorationsCollection | null | undefined>;
  /** Legacy "mechanism" | "electronic" snippets use the setup zone when no explicit dropZone is provided. */
  snippetType?: string;
  dropZone?: CodeSnippetDropZoneMode;
  codeByDropZone?: CodeSnippetCodeByDropZone;
  frameworkClassName?: string;
}

export function insertPreviewSnippet({
  editor,
  textToInsert,
  cursorPos,
  lastPreviewRef,
  draggingDecorationRef,
  snippetType,
  dropZone,
  codeByDropZone,
  frameworkClassName,
}: InsertPreviewSnippetParams): void {
  const model = editor.getModel();
  if (!model) return;

  let totalLines = model.getLineCount();
  let targetLine = Math.min(Math.max(cursorPos.lineNumber, 1), totalLines);

  // Remove previously inserted preview block, if any
  if (lastPreviewRef.current) {
    const lp = lastPreviewRef.current;
    const numLines = lp.numLines ?? 1;
    editor.pushUndoStop();
    editor.executeEdits('preview.delete', [{ range: new monaco.Range(lp.line, 1, lp.line + numLines, 1), text: '', forceMoveMarkers: true }]);
    editor.pushUndoStop();
    // Recompute bounds after deletion
    totalLines = model.getLineCount();
    if (lp.line < targetLine) targetLine -= numLines;
    targetLine = Math.min(Math.max(targetLine, 1), totalLines);
  }

  const insideGroup = isInsideAnyGroup(model, targetLine);
  const isValidZone = isValidSnippetDropZone(model, targetLine, snippetType, dropZone, textToInsert, frameworkClassName);
  const resolvedDropZone = resolveSnippetDropZoneForTarget(model, targetLine, snippetType, dropZone, textToInsert, frameworkClassName);
  const resolvedTextToInsert = (resolvedDropZone && codeByDropZone?.[resolvedDropZone]) || textToInsert;

  // Compute indentation at target line and whether we're near parentheses
  const lineContent = model.getLineContent(targetLine);
  const leadingSpaces = lineContent.match(/^\s*/)?.[0] || '';
  const prev = findPrevNonEmpty(model, targetLine);
  const next = findNextNonEmpty(model, targetLine);
  const prevEndsWithOpen = !!prev && /\(\s*$/.test(prev.content);
  const nextIsCloseParen = !!next && /^\s*\)\s*,?\s*$/.test(next.content);
  // Determine base leading from anchor lines (prev '(' line or next ')' line)
  const prevLeading = prev ? prev.content.match(/^\s*/)?.[0] || '' : '';
  const nextLeading = next ? next.content.match(/^\s*/)?.[0] || '' : '';
  const baseLeading = prevEndsWithOpen ? prevLeading : nextIsCloseParen ? nextLeading : leadingSpaces;
  // Add one extra indent only if current target line hasn't already been indented beyond base
  let extraIndent = '';
  const shouldAddExtraIndent = insideGroup && (prevEndsWithOpen || nextIsCloseParen) && leadingSpaces === baseLeading;
  if (shouldAddExtraIndent) {
    extraIndent = /\t/.test(baseLeading) ? '\t' : '  ';
  }

  // Indent the preview snippet for display only
  const insertLines = resolvedTextToInsert.split('\n').map((line) => (line.length ? leadingSpaces + extraIndent + line : line));
  const insertText = insertLines.join('\n');

  // Preview only: no extra blanks during drag; spacing is added on actual drop
  const snippetLineCount = insertLines.length;
  const insertWithSpacing = insertText + '\n';
  const numInsertedLines = snippetLineCount;
  const snippetStartLine = targetLine;

  const range = new monaco.Range(targetLine, 1, targetLine, 1);
  editor.pushUndoStop();
  editor.executeEdits('preview.insert', [{ range, text: insertWithSpacing, forceMoveMarkers: true }]);
  editor.pushUndoStop();
  editor.focus();

  lastPreviewRef.current = { line: targetLine, numLines: numInsertedLines, isValidZone, resolvedDropZone };

  // Apply highlight to snippet lines only
  const previewDecorations: monaco.editor.IModelDeltaDecoration[] = [];
  for (let k = 0; k < snippetLineCount; k++) {
    if (isValidZone) {
      previewDecorations.push({
        range: new monaco.Range(snippetStartLine + k, 1, snippetStartLine + k, 1),
        options: { isWholeLine: true, className: 'snippet-highlight' },
      });
    } else {
      previewDecorations.push({
        range: new monaco.Range(snippetStartLine + k, 1, snippetStartLine + k, 1),
        options: { isWholeLine: true, inlineClassName: 'dimmed-inline' },
      });
    }
  }
  if (!draggingDecorationRef.current) draggingDecorationRef.current = editor.createDecorationsCollection(previewDecorations);
  else draggingDecorationRef.current.set(previewDecorations);
}

export interface InsertSnippetAtLineParams {
  editor: monaco.editor.IStandaloneCodeEditor;
  textToInsert: string;
  snippetTextToInsert?: string;
  triggerParameterHints?: boolean;
  targetLine: number;
  imports?: CodeSnippetImport[];
}

function countNewlines(value: string): number {
  return (value.match(/\n/g) ?? []).length;
}

function getPositionAtOffset(model: monaco.editor.ITextModel, code: string, offset: number): monaco.Position {
  if (typeof model.getPositionAt === 'function') {
    return model.getPositionAt(offset);
  }

  const lineStarts = getLineStarts(code);
  let lineIndex = 0;
  for (let index = 0; index < lineStarts.length; index += 1) {
    if (lineStarts[index] <= offset) {
      lineIndex = index;
      continue;
    }
    break;
  }

  return { lineNumber: lineIndex + 1, column: offset - lineStarts[lineIndex] + 1 } as monaco.Position;
}

function offsetEditToMonacoEdit(model: monaco.editor.ITextModel, code: string, edit: OffsetTextEdit): monaco.editor.IIdentifiedSingleEditOperation {
  const start = getPositionAtOffset(model, code, edit.startOffset);
  const end = getPositionAtOffset(model, code, edit.endOffset);
  return {
    range: new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column),
    text: edit.newText,
    forceMoveMarkers: true,
  };
}

function getLineNumberForOffset(lineStarts: number[], offset: number): number {
  let lineIndex = 0;
  for (let index = 0; index < lineStarts.length; index += 1) {
    if (lineStarts[index] <= offset) {
      lineIndex = index;
      continue;
    }
    break;
  }

  return lineIndex + 1;
}

function getAdjustedTargetLineAfterImports(code: string, importEdits: OffsetTextEdit[], targetLine: number): number {
  const lineStarts = getLineStarts(code);
  const targetLineStartOffset = lineStarts[Math.max(targetLine - 1, 0)] ?? code.length;
  let adjustedTargetLine = targetLine;

  for (const edit of importEdits) {
    const replacedText = code.slice(edit.startOffset, edit.endOffset);
    const lineDelta = countNewlines(edit.newText) - countNewlines(replacedText);

    if (edit.startOffset <= targetLineStartOffset) {
      adjustedTargetLine += lineDelta;
      continue;
    }

    const lineAfterInsertedImport =
      edit.startOffset === edit.endOffset
        ? getLineNumberForOffset(lineStarts, edit.startOffset) + countNewlines(edit.newText)
        : getLineNumberForOffset(lineStarts, edit.endOffset) + lineDelta + 1;
    adjustedTargetLine = Math.max(adjustedTargetLine, lineAfterInsertedImport);
  }

  return adjustedTargetLine;
}

function applySnippetImports(editor: monaco.editor.IStandaloneCodeEditor, model: monaco.editor.ITextModel, importEdits: OffsetTextEdit[]): void {
  const code = model.getValue();
  if (importEdits.length === 0) {
    return;
  }

  const importOperations = importEdits.map((edit) => offsetEditToMonacoEdit(model, code, edit));
  editor.executeEdits('insert.snippet.imports', importOperations);
}

function indentSnippetText(snippetText: string, baseLeading: string, extraIndent: string): string {
  return snippetText
    .split('\n')
    .map((ln) => (ln.length ? baseLeading + extraIndent + ln : ln))
    .join('\n');
}

interface MonacoParameterHintsController {
  trigger: (context: { triggerKind: monaco.languages.SignatureHelpTriggerKind }) => void;
}

const PARAMETER_HINT_RETRY_DELAYS_MS = [0, 75, 250, 500];

function getParameterHintsController(editor: monaco.editor.IStandaloneCodeEditor): MonacoParameterHintsController | null {
  const editorWithContributions = editor as monaco.editor.IStandaloneCodeEditor & {
    getContribution?: (id: string) => unknown;
  };
  const contribution = editorWithContributions.getContribution?.('editor.controller.parameterHints');
  if (contribution && typeof contribution === 'object' && 'trigger' in contribution && typeof contribution.trigger === 'function') {
    return contribution as MonacoParameterHintsController;
  }

  return null;
}

function plainTextBeforeFirstSnippetPlaceholder(snippetText: string): string | null {
  const placeholderMatch = snippetText.match(/\$\{[1-9]\d*(?::[^}]*)?\}|\$[1-9]\d*/);
  if (!placeholderMatch || placeholderMatch.index == null) {
    return null;
  }

  return snippetText
    .slice(0, placeholderMatch.index)
    .replace(/\$\{\d+:[^}]*\}/g, '')
    .replace(/\$\d+/g, '');
}

function plainTextBeforeFirstEmptyCallArgument(insertText: string): string | null {
  const callMatch = insertText.match(/\b[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*\(\s*\)/);
  if (!callMatch || callMatch.index == null) {
    return null;
  }

  const openParenOffset = insertText.indexOf('(', callMatch.index);
  return openParenOffset === -1 ? null : insertText.slice(0, openParenOffset + 1);
}

function positionAfterInsertedPrefix(prefix: string | null, startLine: number): monaco.IPosition | null {
  if (prefix == null) {
    return null;
  }

  const lines = prefix.split('\n');
  return {
    lineNumber: startLine + lines.length - 1,
    column: (lines[lines.length - 1] ?? '').length + 1,
  };
}

function isStillInsideEmptySnippetArgument(editor: monaco.editor.IStandaloneCodeEditor, position: monaco.IPosition): boolean {
  const lineContent = editor.getModel()?.getLineContent(position.lineNumber) ?? '';
  const characterBeforeCursor = lineContent[position.column - 2];
  const characterAfterCursor = lineContent[position.column - 1];
  return characterBeforeCursor === '(' && (characterAfterCursor === ')' || characterAfterCursor === ',');
}

function placeCursorAtPosition(editor: monaco.editor.IStandaloneCodeEditor, position: monaco.IPosition): void {
  editor.setSelection(new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column));
  editor.setPosition(position);
}

function triggerParameterHintsFromSnippetPlaceholder(editor: monaco.editor.IStandaloneCodeEditor, position: monaco.IPosition | null): void {
  const trigger = () => {
    editor.focus();
    if (position) {
      if (!isStillInsideEmptySnippetArgument(editor, position)) {
        return;
      }
      placeCursorAtPosition(editor, position);
    }
    editor.trigger('insert.snippet', 'editor.action.triggerParameterHints', {});
    getParameterHintsController(editor)?.trigger({ triggerKind: monaco.languages.SignatureHelpTriggerKind.Invoke });
  };

  const scheduleTrigger = (delayMs: number) => {
    globalThis.setTimeout(trigger, delayMs);
  };

  if (typeof globalThis.requestAnimationFrame === 'function') {
    globalThis.requestAnimationFrame(() => {
      PARAMETER_HINT_RETRY_DELAYS_MS.forEach(scheduleTrigger);
    });
    return;
  }

  PARAMETER_HINT_RETRY_DELAYS_MS.forEach(scheduleTrigger);
}

export function insertSnippetAtLine({
  editor,
  textToInsert,
  snippetTextToInsert,
  triggerParameterHints,
  targetLine,
  imports,
}: InsertSnippetAtLineParams): void {
  const model = editor.getModel();
  if (!model) return;

  const originalCode = model.getValue();
  const importEdits = buildExplicitImportEdits(originalCode, imports ?? []);
  const adjustedTargetLine = getAdjustedTargetLineAfterImports(originalCode, importEdits, targetLine);

  editor.pushUndoStop();
  applySnippetImports(editor, model, importEdits);

  const total = model.getLineCount();
  let finalTargetLine = Math.min(Math.max(adjustedTargetLine, 1), total + 1);
  // If drop target is just above a closing paren of the group, bias insert to that line
  const nextLine = model.getLineContent(Math.min(finalTargetLine, total));
  if (/^\s*\)\s*,?\s*$/.test(nextLine)) {
    finalTargetLine = Math.max(1, finalTargetLine);
  }

  const insideGroup = isInsideAnyGroup(model, finalTargetLine);
  const lineContent = model.getLineContent(Math.min(finalTargetLine, total)) || '';
  const leadingSpaces = lineContent.match(/^\s*/)?.[0] || '';
  const prev = findPrevNonEmpty(model, finalTargetLine);
  const next = findNextNonEmpty(model, finalTargetLine);
  const prevEndsWithOpen = !!prev && /\(\s*$/.test(prev.content);
  const nextIsCloseParen = !!next && /^\s*\)\s*,?\s*$/.test(next.content);
  const prevLeading = prev ? prev.content.match(/^\s*/)?.[0] || '' : '';
  const nextLeading = next ? next.content.match(/^\s*/)?.[0] || '' : '';
  const baseLeading = prevEndsWithOpen ? prevLeading : nextIsCloseParen ? nextLeading : leadingSpaces;
  const extraIndent =
    insideGroup && (prevEndsWithOpen || nextIsCloseParen) && leadingSpaces === baseLeading ? (/\t/.test(baseLeading) ? '\t' : '  ') : '';

  const insertLines = textToInsert.split('\n').map((ln) => (ln.length ? baseLeading + extraIndent + ln : ln));
  const range = new monaco.Range(finalTargetLine, 1, finalTargetLine, 1);

  if (snippetTextToInsert) {
    const snippetText = `${indentSnippetText(snippetTextToInsert, baseLeading, extraIndent)}\n`;
    const insertText = insertLines.join('\n') + '\n';
    const firstPlaceholderPosition = positionAfterInsertedPrefix(plainTextBeforeFirstSnippetPlaceholder(snippetText), finalTargetLine);
    editor.executeEdits('insert.snippet', [{ range, text: insertText, forceMoveMarkers: true }]);
    if (firstPlaceholderPosition) {
      placeCursorAtPosition(editor, firstPlaceholderPosition);
    }
    if (triggerParameterHints) {
      triggerParameterHintsFromSnippetPlaceholder(editor, firstPlaceholderPosition);
    }
  } else {
    const insertText = insertLines.join('\n') + '\n';
    const firstEmptyCallPosition = triggerParameterHints
      ? positionAfterInsertedPrefix(plainTextBeforeFirstEmptyCallArgument(insertText), finalTargetLine)
      : null;
    editor.executeEdits('insert.snippet', [{ range, text: insertText, forceMoveMarkers: true }]);
    if (firstEmptyCallPosition) {
      placeCursorAtPosition(editor, firstEmptyCallPosition);
      triggerParameterHintsFromSnippetPlaceholder(editor, firstEmptyCallPosition);
    }
  }

  editor.pushUndoStop();
  editor.focus();
}
